import { uniqueId } from 'lodash'
import {
    ForwardedRef,
    forwardRef,
    SyntheticEvent,
    useCallback,
    useContext,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react'
import { Position, Range, Transform } from 'types'
import { MediaSprite } from '../../../types'
import { ArtboardContext } from '../board/context'
import { ArtboardLayerCanvasRef, ArtboardLayerRef } from '../types/refs'
import useClipPath from './hooks/use-clip-path'
import useDraggedPosition from './hooks/use-dragged-position'
import useDurationSync from './hooks/use-duration-sync'
import { Canvas, Draggable } from './style'

const DEFAULT_TRANSFORM = { x: 0, y: 0, rotation: 0, scaleX: 1, scaleY: 1 }

export type LayerProps = {
    defaultPosition?: Position
    disabled?: boolean
    highlighted?: boolean
    mirror?: boolean
    persistFocus?: boolean
    timing?: Range
    onContextMenu?: (e: SyntheticEvent<HTMLElement, MouseEvent>) => void
    onDoubleClick?: (e: SyntheticEvent<HTMLElement, MouseEvent>) => void
    onFocusChange?: (isFocused: boolean) => void
    onEllipsisMenu?: (e: SyntheticEvent<HTMLElement, MouseEvent>) => void
    onPositionUpdate?: (position: Position) => void
}

interface Props extends LayerProps {
    defaultPosition: Position
    sprite: MediaSprite
}

const ArtboardLayer = (
    {
        defaultPosition,
        disabled = false,
        highlighted = false,
        mirror = false,
        persistFocus = false,
        sprite,
        timing,
        onContextMenu,
        onDoubleClick,
        onEllipsisMenu,
        onFocusChange,
        onPositionUpdate,
    }: Props,
    ref: ForwardedRef<ArtboardLayerRef>
) => {
    const { fps } = useContext(ArtboardContext)
    const { x: defaultX, y: defaultY, width: defaultWidth, height: defaultHeight } = defaultPosition
    const [transform, setTransform] = useState<Transform>(DEFAULT_TRANSFORM)
    const [isDragging, setIsDragging] = useState<boolean>(false)

    const idRef = useRef<string>(uniqueId('artboard-layer-'))
    const canvasRef = useRef<ArtboardLayerCanvasRef>(null)
    const position = useDraggedPosition(defaultPosition, transform)
    const clipPath = useClipPath(position, isDragging)

    const style = {
        left: Math.floor(defaultX - defaultWidth / 2),
        top: Math.floor(defaultY - defaultHeight / 2),
    }
    const onDragStart = useCallback(() => {
        setIsDragging(true)
    }, [])

    const onDragEnd = useCallback((drag: Transform) => {
        setIsDragging(false)
        setTransform(drag)
    }, [])

    useDurationSync(sprite.numFrames)

    useImperativeHandle(ref, () => ({
        layerId: idRef.current,
        getDuration: () => {
            if (sprite.numFrames < 2) {
                return 0
            }

            return sprite.numFrames / fps
        },
        getMirror: () => mirror,
        getPosition: () => position,
        getSprite: () => sprite,
        getSpriteFrameCoordinates: (frame: number) => {
            return canvasRef.current?.getSpriteFrameCoordinates(frame)
        },
    }))

    useEffect(() => {
        onPositionUpdate?.(position)
    }, [position, onPositionUpdate])

    return sprite && defaultWidth && defaultHeight ? (
        <Draggable
            clipPath={clipPath}
            defaultTransform={transform}
            disabled={disabled}
            height={defaultHeight}
            overflowColor={highlighted ? 'rgba(255, 255, 255, 0.5)' : undefined}
            persistFocus={persistFocus}
            rotateable={true}
            scaleable={true}
            style={style}
            width={defaultWidth}
            onContextMenu={onContextMenu}
            onDoubleClick={onDoubleClick}
            onDragEnd={onDragEnd}
            onDragStart={onDragStart}
            onEllipsisMenu={onEllipsisMenu}
            onFocusChange={onFocusChange}
        >
            <Canvas
                clipPath={clipPath}
                height={position.height}
                mirror={mirror}
                ref={canvasRef}
                sprite={sprite}
                timing={timing}
                width={position.width}
            />
        </Draggable>
    ) : null
}

export default forwardRef(ArtboardLayer)
